import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Alert,
  Typography,
  Box,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ModalGridContainer, ModalFormField } from './common/ModalGrid';

const CustomerFormDialog = ({ open, onClose, onSubmit, customer = null, loading = false }) => {
  const { t } = useTranslation();
  const isEdit = Boolean(customer);

  const [formData, setFormData] = useState({
    firstName: customer?.firstName || '',
    lastName: customer?.lastName || '',
    email: customer?.email || '',
    phone: customer?.phone || '',
    city: customer?.city || '',
    responsiblePerson: customer?.responsiblePerson || '',
    notes: customer?.notes || '',
    lastUsedLang: customer?.lastUsedLang || 'gr',
    sendEmail: true,
    isActive: customer?.isActive !== undefined ? customer.isActive : true
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      if (customer) {
        setFormData({
          firstName: customer.firstName || '',
          lastName: customer.lastName || '',
          email: customer.email || '',
          phone: customer.phone || '',
          city: customer.city || '',
          responsiblePerson: customer.responsiblePerson || '',
          notes: customer.notes || '',
          lastUsedLang: customer.lastUsedLang || 'gr',
          sendEmail: true,
          isActive: customer.isActive !== undefined ? customer.isActive : true
        });
      } else {
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          city: '',
          responsiblePerson: '',
          notes: '',
          lastUsedLang: 'gr',
          sendEmail: true,
          isActive: true
        });
      }
      setErrors({});
    }
  }, [customer, open]);

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.responsiblePerson.trim()) newErrors.responsiblePerson = 'Responsible person is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (validateForm()) onSubmit(formData);
  };

  const handleClose = () => onClose();


  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit
      }}
    >
      <DialogTitle>
        {isEdit ? t('customers.editCustomer') : t('customers.addCustomer')}
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {!isEdit && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">{t('customers.tempPasswordInfo')}</Typography>
          </Alert>
        )}

        {/* Personal Information */}
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
          {t('customers.personalInfo')}
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <ModalGridContainer spacing={2}>
          <ModalFormField>
            <TextField
              name="firstName"
              label={t('customers.firstName')}
              value={formData.firstName}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              error={!!errors.firstName}
              helperText={errors.firstName}
            />
          </ModalFormField>
          <ModalFormField>
            <TextField
              name="lastName"
              label={t('customers.lastName')}
              value={formData.lastName}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              error={!!errors.lastName}
              helperText={errors.lastName}
            />
          </ModalFormField>
        </ModalGridContainer>

        {/* Contact Information */}
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
          {t('customers.contactInfo')}
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <ModalGridContainer spacing={2} sx={{ mb: 4 }}>
          <ModalFormField>
            <TextField
              name="email"
              label={`${t('customers.emailAddress')} *`}
              type="email"
              value={formData.email}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              required
              error={!!errors.email}
              helperText={errors.email}
              disabled={isEdit}
            />
          </ModalFormField>

          <ModalFormField>
            <TextField
              name="phone"
              label={`${t('customers.phoneNumber')} *`}
              value={formData.phone}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              required
              error={!!errors.phone}
              helperText={errors.phone}
            />
          </ModalFormField>

          <ModalFormField>
            <TextField
              name="city"
              label={`${t('customers.city')} *`}
              value={formData.city}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              required
              error={!!errors.city}
              helperText={errors.city}
            />
          </ModalFormField>

          <ModalFormField>
            <TextField
              name="responsiblePerson"
              label={`${t('customers.responsiblePerson')} *`}
              value={formData.responsiblePerson}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              required
              error={!!errors.responsiblePerson}
              helperText={errors.responsiblePerson}
            />
          </ModalFormField>
        </ModalGridContainer>

        {/* Additional Info */}
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
          {t('customers.additionalInfo')}
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <ModalGridContainer spacing={2} sx={{ mb: 4 }}>
          <ModalFormField fullWidth>
            <TextField
              name="notes"
              label={t('customers.notes')}
              value={formData.notes}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              error={!!errors.notes}
              helperText={errors.notes}
            />
          </ModalFormField>
          <ModalFormField>
            <FormControl fullWidth variant="outlined">
              <InputLabel>{t('customers.preferredLanguage')}</InputLabel>
              <Select
                name="lastUsedLang"
                value={formData.lastUsedLang}
                onChange={handleChange}
                label={t('customers.preferredLanguage')}
              >
                <MenuItem value="gr">{t('common.greek')}</MenuItem>
                <MenuItem value="en">{t('common.english')}</MenuItem>
              </Select>
            </FormControl>
          </ModalFormField>
        </ModalGridContainer>

        {/* Settings */}
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
          {t('customers.settings')}
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {!isEdit && (
            <FormControlLabel
              control={<Switch name="sendEmail" checked={formData.sendEmail} onChange={handleChange} color="primary" />}
              label={t('customers.sendWelcomeEmail')}
            />
          )}
          <FormControlLabel
            control={<Switch name="isActive" checked={formData.isActive} onChange={handleChange} color="primary" />}
            label={t('customers.accountActive')}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1, justifyContent: 'flex-end' }}>
        <Button onClick={handleClose} disabled={loading} size="large">
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="contained" disabled={loading} sx={{ minWidth: 120 }}>
          {loading ? t('common.saving') : (isEdit ? t('common.update') : t('common.create'))}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerFormDialog;
